/* @HEADER
 * @version 1.1.960 | 2026-10-26
 * @purpose The command palette's wiring that AppShell held inline: open state, the board's query, Ctrl+K, the archived-task set, the route list, and what a chosen result does. Same behaviour, its own file.
 * @sidecar usePaletteController.ts.header.md
 * @layer app | @hex _none_ | @ctx search
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7942, ZVX-DEV-7685, ZVX-DEV-7414, ZVX-DEV-2053, ZVX-DEV-2067

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { Route } from './routes';
import { useMessages } from '../shared/i18n/useMessages';
import { useCommandPaletteItems } from '../features/kanban/public-api';
import type { SearchItem } from '../features/kanban/public-api';
import { paletteSelectAction } from '../features/kanban/paletteSelectAction';
import { computeArchivedTaskIds } from '../features/kanban/computeArchivedTaskIds';
import { listSprints } from '../features/gantt/sprintStore';
import { emitUiAction, targetHash, useKanbanData, useTaskOverrides } from '@zvenix/tasks';
import { cardPlace, goToCardInItsSpace, locateCardSpace } from '../features/kanban/searchElsewhere';
import { useSearchSpaces } from '../features/kanban/useSearchSpaces';
import type { BoardGroupRecord } from '../lib/db';

/**
 * ZVX-DEV-7414 — WHY THIS FILE EXISTS. Campaign «god-file split», target
 * AppShell.tsx (1414 lines). The palette zone carried the shell's only write of
 * `command-palette-select-task` and its couple with KanbanBoard, so every edit
 * anywhere in the shell selected KanbanBoard's tests. Moved together, they
 * leave the rest of the shell with a narrower radius. The code moved; the
 * behaviour did not.
 *
 * What stays in AppShell: the `<CommandPalette>` mount, and Alt+Left, which
 * used to share the Ctrl+K keydown handler and now has its own.
 */

export interface PaletteControllerOptions {
  onNavigate: (route: Route) => void;
  /** Non-null when a board is on screen: a highlight can go to it directly. */
  activeBoardGroupId: string | null;
  /**
   * ZVX-DEV-7685: the spaces, to name the card's space in the hint and to tell
   * an aggregate view from an ordinary one. Omitted = no hint is ever shown.
   */
  boardGroups?: readonly BoardGroupRecord[];
  /**
   * ZVX-DEV-8233: the project on screen is outside the organisation the switcher names, so the palette is given no cards: it
   * lists the screens and nothing of a project this person is not looking at.
   */
  scopedOut?: boolean;
}

/** The card «Подсветить на доске» found in another space, waiting for the person's answer. */
export interface SearchElsewhere {
  taskId: string;
  spaceId: string;
  spaceName: string;
}

export interface PaletteController {
  open: boolean;
  close: () => void;
  initialQuery: string;
  /** ZVX-DEV-7676: how the palette was opened, told to the diagnostic observer by the palette itself. */
  openVia: 'hotkey' | 'toolbar' | 'board-field';
  items: SearchItem[];
  onSelect: (item: SearchItem, intent?: 'open' | 'highlight') => void;
  /** ZVX-DEV-7685: the hint to show, or null. */
  elsewhere: SearchElsewhere | null;
  /** Enter the card's space and highlight the card. */
  goElsewhere: () => void;
  /** Put the hint away and go nowhere. */
  dismissElsewhere: () => void;
}

export function usePaletteController({
  onNavigate,
  activeBoardGroupId,
  boardGroups,
  scopedOut = false,
}: PaletteControllerOptions): PaletteController {
  const m = useMessages();
  const spaces = useSearchSpaces(boardGroups ?? []);
  const hintEnabled = boardGroups !== undefined;
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteInitialQuery, setPaletteInitialQuery] = useState('');
  const [elsewhere, setElsewhere] = useState<SearchElsewhere | null>(null);
  // The space on screen NOW — the answer about a card's space arrives later.
  const activeSpaceRef = useRef(activeBoardGroupId);
  useEffect(() => {
    activeSpaceRef.current = activeBoardGroupId;
  }, [activeBoardGroupId]);
  // The person moved to another space by themselves: the hint is about the
  // space they left, put it away (state adjusted while rendering, not in an effect).
  const [hintSpace, setHintSpace] = useState(activeBoardGroupId);
  if (hintSpace !== activeBoardGroupId) {
    setHintSpace(activeBoardGroupId);
    setElsewhere(null);
  }
  const [paletteOpenVia, setPaletteOpenVia] = useState<'hotkey' | 'toolbar' | 'board-field'>('toolbar');

  // ── Command palette data ──
  const { kanban, refresh: refreshKanban } = useKanbanData();
  const { overrides: paletteOverrides, refresh: refreshOverrides } = useTaskOverrides();

  // ── Archived (closed-sprint) task ids for the «Архив» search badge (ZVX-DEV-2067) ──
  // The badge marks cards that were Done/Cancelled when a sprint was CLOSED — the
  // durable signal lives in each closed sprint's `snapshot_at_close.tasks`, not in
  // the card's current live status. Loaded from the sprint store and recomputed on
  // the same triggers as the kanban data so the palette stays in sync.
  const [archivedTaskIds, setArchivedTaskIds] = useState<ReadonlySet<string>>(() => new Set());
  const refreshArchived = useCallback(() => {
    listSprints(null, { hideDeleted: true })
      .then((sprints) => setArchivedTaskIds(computeArchivedTaskIds(sprints)))
      .catch(() => setArchivedTaskIds(new Set()));
  }, []);
  useEffect(() => {
    refreshArchived();
  }, [refreshArchived]);

  // ── Command palette: route list for search ──
  const paletteRoutes = useMemo(
    () => [
      { route: 'tasks', label: m.nav.kanban },
      { route: 'canvas', label: m.canvas.sidebarLabel },
      { route: 'structure', label: m.structure.sidebarLabel },
      { route: 'events', label: m.nav.events },
      { route: 'diary', label: m.nav.diary },
      { route: 'analytics', label: m.nav.analytics },
      { route: 'okr', label: m.nav.okr },
      { route: 'settings', label: m.nav.settings },
    ],
    [m],
  );

  const paletteItems = useCommandPaletteItems(
    scopedOut ? null : kanban,
    paletteRoutes,
    paletteOverrides,
    archivedTaskIds,
  );

  // ── Global hotkey: Ctrl+K / Cmd+K ──
  useEffect(() => {
    function handleGlobalKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        refreshKanban();
        refreshOverrides();
        refreshArchived();
        setPaletteOpenVia('hotkey');
        setPaletteOpen((prev) => !prev);
        setPaletteInitialQuery('');
      }
    }
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [refreshKanban, refreshOverrides, refreshArchived]);

  // ── Custom event listener: board search field can open palette ──
  useEffect(() => {
    function handleOpenPalette(e: Event) {
      const detail = (e as CustomEvent<{ query?: string }>).detail;
      setPaletteInitialQuery(detail?.query ?? '');
      setPaletteOpenVia(detail?.query ? 'board-field' : 'toolbar');
      refreshKanban(); // re-fetch data so palette sees locally created/edited tasks
      refreshOverrides(); // re-fetch overrides so newly added labels become searchable
      refreshArchived(); // re-compute closed-sprint archived set for the «Архив» badge
      setPaletteOpen(true);
    }
    window.addEventListener('open-command-palette', handleOpenPalette);
    return () => window.removeEventListener('open-command-palette', handleOpenPalette);
  }, [refreshKanban, refreshOverrides, refreshArchived]);

  // ZVX-DEV-2053: a task result carries an explicit intent.
  //  - 'open'      → open the card detail modal. Reuses the host's robust
  //                  `navigateToTaskInHost` flow (resolve board → mount → open
  //                  modal) via the `open-task-in-host` event, so an open works
  //                  even when the card's board is not currently mounted. This is
  //                  also the row-body click / keyboard-Enter default.
  //  - 'highlight' → scroll the card into view on the board and pulse it WITHOUT
  //                  opening the modal, via the existing `command-palette-select-task`
  //                  event handled by KanbanBoard.highlightTaskFromSearch.
  const hintIfElsewhere = useCallback(
    async (taskId: string) => {
      if (!hintEnabled || !activeBoardGroupId) return;
      const cardSpaceId = await locateCardSpace(taskId).catch(() => null);
      if (activeSpaceRef.current !== activeBoardGroupId) return; // the person left meanwhile
      const place = cardPlace(activeBoardGroupId, cardSpaceId, spaces.isAggregate(activeBoardGroupId));
      const spaceName = cardSpaceId ? spaces.nameOf(cardSpaceId) : null;
      // ZVX-DEV-7676: no board of this space answers a card of another one, so the press's one record comes from here.
      if (place === 'elsewhere') {
        // ZVX-DEV-7942: the service number of the card, so this record can be matched to the press that made it.
        const hash = targetHash(taskId);
        emitUiAction({ type: 'search.locate', intent: 'highlight', place: 'not-in-space', ...(hash ? { targetHash: hash } : {}) });
      }
      if (place === 'elsewhere' && cardSpaceId && spaceName) {
        setElsewhere({ taskId, spaceId: cardSpaceId, spaceName });
      }
    },
    [spaces, hintEnabled, activeBoardGroupId],
  );

  const goToCardSpaceOrTasks = useCallback(
    async (taskId: string) => {
      const cardSpaceId = await locateCardSpace(taskId).catch(() => null);
      if (!cardSpaceId || !goToCardInItsSpace(taskId, cardSpaceId)) onNavigate('tasks');
    },
    [onNavigate],
  );

  const goElsewhere = useCallback(() => {
    if (!elsewhere) return;
    goToCardInItsSpace(elsewhere.taskId, elsewhere.spaceId);
    setElsewhere(null);
  }, [elsewhere]);
  const dismissElsewhere = useCallback(() => setElsewhere(null), []);

  const handlePaletteSelect = useCallback(
    (item: SearchItem, intent: 'open' | 'highlight' = 'open') => {
      const action = paletteSelectAction(item, intent);
      switch (action.kind) {
        case 'open':
          // Open the card detail modal — host resolves the board and opens it.
          window.dispatchEvent(
            new CustomEvent('open-task-in-host', { detail: { taskId: action.taskId } }),
          );
          break;
        case 'highlight':
          setElsewhere(null); // a new press replaces an old hint
          // Highlight-on-board: needs a mounted KanbanBoard listener.
          if (activeBoardGroupId) {
            // Already on a space — dispatch directly; every board of it answers for
            // its own cards, a folded one through the space page (ZVX-DEV-7684).
            window.dispatchEvent(
              new CustomEvent('command-palette-select-task', { detail: { taskId: action.taskId } }),
            );
            // ZVX-DEV-7685: a card of ANOTHER space is answered by no board here — say where it is.
            void hintIfElsewhere(action.taskId);
          } else {
            // ZVX-DEV-7685: outside any space — straight to the card's own space, which takes the
            // card when its data holds it. No board is mounted to hear an event, and none is awaited.
            void goToCardSpaceOrTasks(action.taskId);
          }
          break;
        case 'navigate':
          if (action.route) onNavigate(action.route as Route);
          break;
        // 'none' — task result without a taskId, or non-actionable type. Just close.
        default:
          break;
      }
      setPaletteOpen(false);
    },
    [onNavigate, activeBoardGroupId, hintIfElsewhere, goToCardSpaceOrTasks],
  );

  const close = useCallback(() => setPaletteOpen(false), []);

  return {
    open: paletteOpen,
    close,
    initialQuery: paletteInitialQuery,
    openVia: paletteOpenVia,
    items: paletteItems,
    onSelect: handlePaletteSelect,
    elsewhere,
    goElsewhere,
    dismissElsewhere,
  };
}
